import { useEffect, useCallback } from 'react'
import { useMsal, useIsAuthenticated } from '@azure/msal-react'
import { useAuthStore, type AuthUser } from '@/stores/authStore'
import { loginRequest, msalConfigured } from '@/auth/msalConfig'

const MOCK_AUTH = import.meta.env.VITE_MOCK_AUTH === 'true'
const MOCK_USER: AuthUser = {
  username: 'dev',
  email: 'dev@aptean.com',
  name: 'Dev User',
  is_superuser: true,
}

/** Call once at the app root to hydrate auth state from MSAL. */
export function useAuthInit() {
  const { accounts } = useMsal()
  const msalAuthenticated = useIsAuthenticated()
  const { setUser, clearUser, isAuthenticated } = useAuthStore()

  useEffect(() => {
    if (MOCK_AUTH) {
      setUser(MOCK_USER)
      return
    }
    if (!msalConfigured) {
      clearUser()
      return
    }
    if (msalAuthenticated && accounts.length > 0) {
      const acct = accounts[0]
      const claims = (acct.idTokenClaims ?? {}) as Record<string, unknown>
      const email =
        (claims.preferred_username as string | undefined) ??
        (claims.upn as string | undefined) ??
        (claims.email as string | undefined) ??
        acct.username ??
        ''
      const user: AuthUser = {
        username: (email.split('@')[0] || acct.name || 'user').slice(0, 150),
        email,
        name: acct.name ?? email,
        is_superuser: false,
      }
      if (!isAuthenticated) setUser(user)
    } else {
      if (isAuthenticated) clearUser()
    }
  }, [msalAuthenticated, accounts, isAuthenticated]) // eslint-disable-line react-hooks/exhaustive-deps
}

/** Use in any component that needs auth state or actions. */
export function useAuth() {
  const store = useAuthStore()
  const { instance } = useMsal()

  const loginWithSSO = useCallback(
    (email?: string) => {
      if (MOCK_AUTH || !msalConfigured) return
      void instance.loginRedirect({
        ...loginRequest,
        loginHint: email,
      })
    },
    [instance],
  )

  const logout = useCallback(async () => {
    store.clearUser()
    if (MOCK_AUTH || !msalConfigured) {
      window.location.href = '/login'
      return
    }
    await instance.logoutRedirect({
      postLogoutRedirectUri: `${window.location.origin}/login`,
    })
  }, [instance, store])

  return { ...store, loginWithSSO, logout }
}

